import React, { useState, useEffect } from "react";
import { FaEnvelope, FaInstagram } from "react-icons/fa";
import { supabase } from "../../lib/supabase";
import { IntakeFormData, STEPS, StepIndex } from "../../types/intake";
import { analytics, EVENTS, EVENT_FN } from "../../analytics";
import TermsConsent from "../../components/TermsConsent";

function useSearchParam(key: string): string | null {
  // HashRouter 환경: window.location.hash = "#/intake?session_id=xxx"
  const hash = window.location.hash; // e.g. http://localhost:3000/#/intake?service=premium
  const queryStart = hash.indexOf("?");
  if (queryStart === -1) return null;
  const params = new URLSearchParams(hash.slice(queryStart));
  return params.get(key);
}

const SERVICE_LABEL: Record<string, string> = {
  diagnosis: "진단",
  core:      "핵심",
  signature: "시그니처",
};

const EMPTY_FORM: IntakeFormData = {
  name: "",
  email: "",
  linkedin_url: "",
  target_position: "",
  target_country: "",
  work_experience_years: "",
  industry: "",
  current_situation: "",
  main_concern: "",
  acquisition_channel: "",
  cv_file: null,
  cover_letter_file: null,
  jd_file: null,
};

const TEST_FORM: IntakeFormData = {
  name: "Test User",
  email: "test@bemeconsulting.com",
  linkedin_url: "https://linkedin.com/in/testuser",
  target_position: "Product Manager at UK tech company",
  target_country: "UK",
  work_experience_years: "4",
  industry: "IT/Software",
  current_situation: "현재 한국 IT 기업 재직 중, 영국 이직 준비 중입니다.",
  main_concern: "UK 채용 기준에 맞는 CV 작성 방법이 막막합니다.",
  acquisition_channel: "Instagram",
  cv_file: null,
  cover_letter_file: null,
  jd_file: null,
};

// ─── 단계 표시기 ──────────────────────────────────────────────────────────────

const StepIndicator: React.FC<{ currentStep: StepIndex }> = ({ currentStep }) => (
  <div className="flex items-center justify-center gap-0 mb-10">
    {STEPS.map((label, i) => (
      <React.Fragment key={label}>
        <div className="flex flex-col items-center gap-1">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
              i < currentStep
                ? "bg-accent text-white"
                : i === currentStep
                ? "bg-main text-white"
                : "bg-gray-200 text-gray-400"
            }`}
          >
            {i < currentStep ? "✓" : i + 1}
          </div>
          <span
            className={`text-xs whitespace-nowrap ${
              i === currentStep ? "text-main font-semibold" : "text-gray-400"
            }`}
          >
            {label}
          </span>
        </div>
        {i < STEPS.length - 1 && (
          <div
            className={`h-0.5 w-12 mx-1 mb-5 transition-colors ${
              i < currentStep ? "bg-accent" : "bg-gray-200"
            }`}
          />
        )}
      </React.Fragment>
    ))}
  </div>
);

// ─── Step 1: 기본 정보 ────────────────────────────────────────────────────────

const BasicInfoStep: React.FC<{
  form: IntakeFormData;
  onChange: (field: keyof IntakeFormData, value: string) => void;
  onNext: () => void;
  serviceType: string;
}> = ({ form, onChange, onNext, serviceType }) => {
  const isValid = form.name.trim() && form.email.trim() && form.target_position.trim();

  return (
    <div>
      <div className="space-y-5">
        <Field label="이름 *">
          <input
            type="text"
            value={form.name}
            onChange={(e) => onChange("name", e.target.value)}
            placeholder="홍길동"
            className={inputCls}
          />
        </Field>

        <Field label="이메일 *">
          <input
            type="email"
            value={form.email}
            onChange={(e) => onChange("email", e.target.value)}
            placeholder="example@email.com"
            className={inputCls}
          />
        </Field>

        <Field label="LinkedIn URL">
          <input
            type="url"
            value={form.linkedin_url}
            onChange={(e) => onChange("linkedin_url", e.target.value)}
            placeholder="https://linkedin.com/in/yourname"
            className={inputCls}
          />
        </Field>

        <Field label="지원 희망 직무 / 포지션 *" hint="예: Software Engineer at fintech startups">
          <input
            type="text"
            value={form.target_position}
            onChange={(e) => onChange("target_position", e.target.value)}
            placeholder="예: Product Manager at UK tech companies"
            className={inputCls}
          />
        </Field>

        <Field label="희망 취업 국가">
          <input
            type="text"
            value={form.target_country}
            onChange={(e) => onChange("target_country", e.target.value)}
            placeholder="예: UK, 영국"
            className={inputCls}
          />
        </Field>
      </div>

      <button
        onClick={onNext}
        disabled={!isValid}
        className="mt-8 w-full py-3 bg-main text-white font-semibold rounded-lg hover:bg-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        다음 단계 →
      </button>
    </div>
  );
};

// ─── Step 2: 현재 상황 ────────────────────────────────────────────────────────

const ServiceInfoStep: React.FC<{
  form: IntakeFormData;
  onChange: (field: keyof IntakeFormData, value: string) => void;
  onNext: () => void;
  onBack: () => void;
}> = ({ form, onChange, onNext, onBack }) => {
  const isValid = form.current_situation.trim() && form.main_concern.trim();

  return (
    <div>
      <div className="space-y-5">
        <Field label="총 경력 연수">
          <select
            value={form.work_experience_years}
            onChange={(e) => onChange("work_experience_years", e.target.value)}
            className={inputCls}
          >
            <option value="">선택해주세요</option>
            <option value="0">경력 없음 (신입)</option>
            <option value="1">1년 미만</option>
            <option value="2">1~3년</option>
            <option value="4">3~5년</option>
            <option value="6">5~10년</option>
            <option value="11">10년 이상</option>
          </select>
        </Field>

        <Field label="현재 또는 이전 업종 / 직무">
          <input
            type="text"
            value={form.industry}
            onChange={(e) => onChange("industry", e.target.value)}
            placeholder="예: IT/소프트웨어, 금융, 마케팅"
            className={inputCls}
          />
        </Field>

        <Field
          label="현재 상황을 간략히 설명해주세요 *"
          hint="현재 어떤 상황에서 이 서비스를 신청하셨나요?"
        >
          <textarea
            value={form.current_situation}
            onChange={(e) => onChange("current_situation", e.target.value)}
            placeholder="예: 현재 한국에서 IT 기업을 다니고 있고 영국으로 이직을 준비 중입니다. CV를 여러 번 지원했지만 서류 탈락이 반복되고 있어요."
            rows={4}
            className={inputCls}
          />
        </Field>

        <Field
          label="가장 큰 고민이나 도움받고 싶은 부분 *"
          hint="구체적일수록 더 정확한 도움을 드릴 수 있습니다"
        >
          <textarea
            value={form.main_concern}
            onChange={(e) => onChange("main_concern", e.target.value)}
            placeholder="예: 영국 채용 기준에 맞게 CV를 어떻게 써야 할지 모르겠고, 커버레터에서 무엇을 강조해야 할지 막막합니다."
            rows={4}
            className={inputCls}
          />
        </Field>

        <Field label="BeMe를 처음으로 어떻게 알게 되셨나요?">
          <select
            value={form.acquisition_channel}
            onChange={(e) => onChange("acquisition_channel", e.target.value)}
            className={inputCls}
          >
            <option value="">선택해주세요</option>
            <option value="Instagram">Instagram</option>
            <option value="LinkedIn">LinkedIn</option>
            <option value="Threads">Threads</option>
            <option value="유튜브">유튜브</option>
            <option value="네이버 블로그">네이버 블로그</option>
            <option value="website">웹사이트 검색</option>
            <option value="지인 추천">지인 추천</option>
            <option value="기타">기타</option>
          </select>
        </Field>
      </div>

      <div className="mt-8 flex gap-3">
        <button
          onClick={onBack}
          className="flex-1 py-3 border border-main text-main font-semibold rounded-lg hover:bg-sub transition-colors"
        >
          ← 이전
        </button>
        <button
          onClick={onNext}
          disabled={!isValid}
          className="flex-1 py-3 bg-main text-white font-semibold rounded-lg hover:bg-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          다음 단계 →
        </button>
      </div>
    </div>
  );
};

// ─── Step 3: 파일 첨부 ────────────────────────────────────────────────────────

const FileUploadStep: React.FC<{
  form: IntakeFormData;
  serviceType: string;
  onFileChange: (field: "cv_file" | "cover_letter_file" | "jd_file", file: File | null) => void;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting: boolean;
  fileUploadError: string | null;
}> = ({ form, serviceType, onFileChange, onSubmit, onBack, isSubmitting, fileUploadError }) => {
  const isSignature = serviceType === "signature";
  const [agreed, setAgreed] = useState(false);
  const handleFile = (field: "cv_file" | "cover_letter_file" | "jd_file") => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    onFileChange(field, file);
  };

  return (
    <div>
      <p className="text-sm text-gray-500 mb-6">
        파일을 첨부해주세요. PDF 또는 Word 파일(.pdf, .doc, .docx)만 가능합니다.
        파일 첨부는 선택사항이며, 나중에 이메일로도 보내주실 수 있습니다.
      </p>

      <div className="space-y-5">
        <FileField
          label="CV 파일"
          file={form.cv_file}
          onChange={handleFile("cv_file")}
          accept=".pdf,.doc,.docx"
        />
        {isSignature && (
          <FileField
            label="커버레터 파일 (선택)"
            file={form.cover_letter_file}
            onChange={handleFile("cover_letter_file")}
            accept=".pdf,.doc,.docx"
          />
        )}
        {isSignature && (
          <FileField
            label="JD (Job Description) 파일 (선택)"
            file={form.jd_file}
            onChange={handleFile("jd_file")}
            accept=".pdf,.doc,.docx"
          />
        )}
      </div>

      {fileUploadError && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          ⚠️ {fileUploadError}
          <p className="text-xs text-red-400 mt-1">파일 없이 제출하거나, 잠시 후 다시 시도해주세요.</p>
        </div>
      )}

      {/* Terms & Consent */}
      <div className="mt-6">
        <TermsConsent checked={agreed} onChange={setAgreed} />
      </div>

      <div className="mt-4 flex gap-3">
        <button
          onClick={onBack}
          disabled={isSubmitting}
          className="flex-1 py-3 border border-main text-main font-semibold rounded-lg hover:bg-sub transition-colors disabled:opacity-40"
        >
          ← 이전
        </button>
        <button
          onClick={onSubmit}
          disabled={isSubmitting || !agreed}
          className="flex-1 py-3 bg-accent text-white font-semibold rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSubmitting ? "제출 중..." : "제출하기 ✓"}
        </button>
      </div>
    </div>
  );
};

// ─── 완료 화면 ────────────────────────────────────────────────────────────────

const SERVICE_DAYS: Record<string, number> = {
  diagnosis: 5, core: 7, signature: 14,
};

const TURNAROUND: Record<string, string> = {
  diagnosis: "영업일 3일",
  core:      "영업일 5일",
  signature: "영업일 5~10일",
};

function formatKoreanDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
}

const SuccessScreen: React.FC<{ name: string; serviceType: string; dueDate: string | null }> = ({ name, serviceType, dueDate }) => {
  const turnaround = TURNAROUND[serviceType.toLowerCase()] ?? "빠른 시일 내";
  return (
    <div className="flex-1 bg-sub flex flex-col items-center justify-center px-4 pt-28 pb-8 text-center">
      <div className="bg-white rounded-2xl shadow-md p-10 w-full max-w-lg">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5 text-3xl">
          ✓
        </div>
        <h1 className="text-2xl font-bold text-main mb-3">제출 완료!</h1>
        <p className="text-gray-600 mb-4">
          <span className="font-semibold">{name}</span>님, 신청서가 성공적으로 접수되었습니다.
        </p>

        <div className="bg-sub rounded-xl px-5 py-4 mb-5 text-left">
          <p className="text-xs text-gray-400 mb-1">예상 결과물 전달</p>
          <p className="text-main font-bold text-lg">{turnaround}</p>
          {dueDate && (
            <p className="text-main font-semibold text-sm mt-1">
              ~ {formatKoreanDate(dueDate)} 까지
            </p>
          )}
          <p className="text-xs text-gray-400 mt-1">접수 완료 후 영업일 기준</p>
        </div>

        <p className="text-gray-500 text-sm mb-4">
          신청 확인 이메일을 보내드렸습니다. 추가 문의나 파일 전송은 아래로 연락주세요.
        </p>
        <div className="flex items-center justify-center gap-3 text-sm text-gray-600">
          <a
            href="mailto:info@bemeconsulting.com"
            className="flex items-center gap-1.5 hover:text-main transition-colors"
          >
            <FaEnvelope className="text-accent" />
            info@bemeconsulting.com
          </a>
          <span className="text-gray-300">|</span>
          <a
            href="https://instagram.com/beme.consulting"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 hover:text-main transition-colors"
          >
            <FaInstagram className="text-accent" />
            @beme.consulting
          </a>
        </div>
      </div>
    </div>
  );
};

// ─── 공통 서브 컴포넌트 ──────────────────────────────────────────────────────

const inputCls =
  "w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-main/30 focus:border-main transition";

const Field: React.FC<{
  label: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, hint, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {hint && <p className="text-xs text-gray-400 mb-1">{hint}</p>}
    {children}
  </div>
);

const FileField: React.FC<{
  label: string;
  file: File | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  accept: string;
}> = ({ label, file, onChange, accept }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
    <label className="flex items-center gap-3 border-2 border-dashed border-gray-200 rounded-lg px-4 py-3 cursor-pointer hover:border-main transition-colors">
      <span className="text-2xl">📎</span>
      <span className="text-sm text-gray-500 flex-1 truncate">
        {file ? file.name : "클릭하여 파일 선택"}
      </span>
      <input type="file" accept={accept} onChange={onChange} className="hidden" />
    </label>
  </div>
);

// ─── 메인 IntakePage ──────────────────────────────────────────────────────────

const IntakePage: React.FC = () => {
  const sessionId = useSearchParam("session_id");
  const serviceType = useSearchParam("service") ?? "unknown";

  const [step, setStep] = useState<StepIndex>(0);
  const [form, setForm] = useState<IntakeFormData>(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);
  const [dueDate, setDueDate] = useState<string | null>(null);

  // session_id 없으면 임시 ID 생성 (직접 링크 접속 허용)
  const effectiveSessionId = sessionId ?? `manual_${Date.now()}`;

  // Fire purchase conversion event once per session on page load.
  // form_start fires when user arrives at intake (Stripe payment done).
  // purchase fires only after form submit — the true completed conversion.
  useEffect(() => {
    const dedupeKey = `beme_start_fired_${effectiveSessionId}`;
    if (sessionStorage.getItem(dedupeKey)) return;
    sessionStorage.setItem(dedupeKey, "1");
    analytics.track(EVENT_FN.intakeFunnel("start", serviceType));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field: keyof IntakeFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (field: "cv_file" | "cover_letter_file" | "jd_file", file: File | null) => {
    setForm((prev) => ({ ...prev, [field]: file }));
  };

  const sanitizeFileName = (name: string): string => {
    const ext = name.slice(name.lastIndexOf("."));
    const base = name.slice(0, name.lastIndexOf("."));
    const safe = base.replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 50);
    return `${safe}${ext}`;
  };

  const uploadFile = async (file: File, path: string): Promise<string | null> => {
    const { data, error } = await supabase.storage
      .from("intake-files")
      .upload(path, file);
    if (error) {
      setFileUploadError(`파일 업로드 실패 (${file.name}): ${error.message}`);
      return null;
    }
    const { data: urlData } = supabase.storage.from("intake-files").getPublicUrl(data.path);
    return urlData.publicUrl;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // 파일 업로드
      let cvUrl: string | null = null;
      let clUrl: string | null = null;
      let jdUrl: string | null = null;

      if (form.cv_file) {
        cvUrl = await uploadFile(form.cv_file, `${effectiveSessionId}/cv_${Date.now()}_${sanitizeFileName(form.cv_file.name)}`);
      }
      if (form.cover_letter_file) {
        clUrl = await uploadFile(
          form.cover_letter_file,
          `${effectiveSessionId}/cl_${Date.now()}_${sanitizeFileName(form.cover_letter_file.name)}`
        );
      }
      if (form.jd_file) {
        jdUrl = await uploadFile(
          form.jd_file,
          `${effectiveSessionId}/jd_${Date.now()}_${sanitizeFileName(form.jd_file.name)}`
        );
      }

      // RPC 호출 (중복 체크 + INSERT 포함)
      const payload = {
        stripe_session_id: effectiveSessionId,
        service_type: serviceType,
        name: form.name.trim(),
        email: form.email.trim(),
        linkedin_url: form.linkedin_url.trim(),
        target_position: form.target_position.trim(),
        target_country: form.target_country.trim(),
        work_experience_years: form.work_experience_years,
        industry: form.industry.trim(),
        current_situation: form.current_situation.trim(),
        main_concern: form.main_concern.trim(),
        cv_file_url: cvUrl ?? "",
        cover_letter_file_url: clUrl ?? "",
        jd_file_url: jdUrl ?? "",
        acquisition_channel: form.acquisition_channel,
      };

      const { data: rpcResult, error } = await supabase.rpc("submit_intake", { payload });

      if (error) throw error;

      if (rpcResult?.error === "duplicate") {
        setSubmitError(rpcResult.message);
        return;
      }

      analytics.track(EVENT_FN.intakeFunnel("submit", serviceType));
      analytics.track(EVENTS.PURCHASE, { service_type: serviceType, session_id: effectiveSessionId });

      // due_date 계산 (서비스별 캘린더 일수)
      const days = SERVICE_DAYS[serviceType.toLowerCase()] ?? 7;
      const due = new Date();
      due.setDate(due.getDate() + days);
      const dueDateStr = due.toISOString().slice(0, 10); // "YYYY-MM-DD"
      setDueDate(dueDateStr);
      setIsDone(true);

      // 확인 이메일 발송 (client + admin, fire-and-forget)
      supabase.functions.invoke("send-intake-email", {
        body: {
          name: payload.name,
          email: payload.email,
          service_type: payload.service_type,
          target_position: payload.target_position,
          target_country: payload.target_country,
          work_experience_years: payload.work_experience_years,
          industry: payload.industry,
          current_situation: payload.current_situation,
          main_concern: payload.main_concern,
          cv_file_url: cvUrl ?? "",
          cover_letter_file_url: clUrl ?? "",
          jd_file_url: jdUrl ?? "",
          due_date: dueDateStr,
          customer_id: rpcResult?.customer_id ?? null,
        },
      }).catch(() => {});
    } catch (err: any) {
      supabase.from("error_logs").insert({
        source: "client_intake",
        error_msg: err?.message ?? String(err),
        payload: { service_type: serviceType, email: form.email, session_id: effectiveSessionId },
      }).then(() => {});
      setSubmitError("제출 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isDone) return <SuccessScreen name={form.name} serviceType={serviceType} dueDate={dueDate} />;

  return (
    <div className="flex-1 bg-sub flex flex-col items-center justify-center px-4 pt-28 pb-8">
      <div className="w-full max-w-lg">
        {/* 헤더 */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-main mb-4">BeMe {SERVICE_LABEL[serviceType.toLowerCase()] ?? serviceType} 서비스 신청서</h1>
          <p className="text-sm text-gray-500 mt-1">
            결제가 완료되었습니다. 서비스 시작을 위해 아래 정보를 입력해주세요.
          </p>
        </div>

        {/* 폼 카드 */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          {process.env.NODE_ENV === "development" && (
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => { setForm(TEST_FORM); setStep(0); }}
                className="flex-1 py-2 text-xs bg-yellow-50 border border-yellow-300 text-yellow-700 rounded-lg hover:bg-yellow-100 transition-colors"
              >
                [DEV] 전체 테스트
              </button>
              <button
                onClick={() => { setForm(TEST_FORM); setStep(2); setFileUploadError(null); }}
                className="flex-1 py-2 text-xs bg-blue-50 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
              >
                [DEV] 파일 업로드 테스트
              </button>
            </div>
          )}
          <StepIndicator currentStep={step} />

          {step === 0 && (
            <BasicInfoStep
              form={form}
              onChange={handleChange}
              onNext={() => { setStep(1); analytics.track(EVENT_FN.intakeFunnel("step1", serviceType)); }}
              serviceType={serviceType}
            />
          )}
          {step === 1 && (
            <ServiceInfoStep
              form={form}
              onChange={handleChange}
              onNext={() => { setStep(2); analytics.track(EVENT_FN.intakeFunnel("step2", serviceType)); }}
              onBack={() => setStep(0)}
            />
          )}
          {step === 2 && (
            <FileUploadStep
              form={form}
              serviceType={serviceType}
              onFileChange={handleFileChange}
              onSubmit={handleSubmit}
              onBack={() => setStep(1)}
              isSubmitting={isSubmitting}
              fileUploadError={fileUploadError}
            />
          )}

          {submitError && (
            <p className="mt-4 text-sm text-red-500 text-center">{submitError}</p>
          )}
        </div>

        
      </div>
    </div>
  );
};

export default IntakePage;
